import { Component, computed, inject, input, signal } from '@angular/core';
import { UserBoxComponent } from '../user-box/user-box.component';
import { FeComponentLibraryModule, ThemeModule } from '@wind-log_library/fe-component-library';
import { CommonModule } from '@angular/common';
import { IconsPipe } from '@pipes/icons/icons.pipe';
import { NavigationService } from '@services/navigation/navigation.service';
import { NavigationEnd, Router } from '@angular/router';
import { Device, NavigationPopovers } from 'src/app/modules/shared/models/navigation.interface';
import { NavigationDataService } from '@services/navigation-data/navigation-data.service';
import { LocalStorageService } from '@services/local-storage-service/local-storage.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, filter, map, of, startWith, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ColorcodeLegendComponent } from '../colorcode-legend/colorcode-legend.component';
import { deviceStatusSortRank } from 'src/app/modules/shared/constants/device-status.constant';

@Component({
  selector: 'app-navigation-component',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  imports: [
    UserBoxComponent,
    ThemeModule,
    FeComponentLibraryModule,
    IconsPipe,
    CommonModule,
    MatTooltipModule,
    MatMenuModule,
    FormsModule,
    ColorcodeLegendComponent,
  ],
})
export class NavigationComponent {
  private localStorageService = inject(LocalStorageService);
  private navigationDataService = inject(NavigationDataService);
  navigationService = inject(NavigationService);
  private router = inject(Router);

  countryList = computed(
    () =>
      this.navigationService
        .countryListSignal()()
        ?.toSorted((a, b) => a?.name.replace(/\s/g, '').localeCompare(b.name.replace(/\s/g, ''))) || [],
  );
  deviceTypesListSig = this.navigationDataService.getDeviceTypes();
  deviceTypesList = computed(() => this.deviceTypesListSig());
  selectedCountry = computed(() => this.navigationService.selectedCountry());
  selectedRegion = computed(() => this.navigationService.selectedRegion());
  filteredSites = computed(() =>
    this.navigationService
      .filteredSites()()
      .slice()
      .sort((a, b) => a?.name.replace(/\s/g, '').localeCompare(b.name.replace(/\s/g, ''))),
  );
  selectedSite = computed(() => this.navigationService.selectedSite());
  deviceStatuses = toSignal(
    toObservable(computed(() => this.selectedSite()?._id)).pipe(
      filter((siteId): siteId is string => Boolean(siteId)),
      distinctUntilChanged(),
      switchMap(siteId =>
        this.navigationDataService.getSiteDeviceStatuses(siteId).pipe(
          map(devices => new Map<string, string>(devices.map(device => [device._id, device.status ?? 'UNKNOWN']))),
          catchError(() => of(new Map<string, string>())),
        ),
      ),
    ),
    { initialValue: new Map<string, string>() },
  );
  selectedDevice = computed(() => {
    const devices = this.devicesList();
    const selectedDeviceId = this.navigationService.deviceIdSig();

    return devices.find(device => device._id === selectedDeviceId) ?? devices[0];
  });
  selectedDeviceIndex = computed(() => {
    return this.devicesList().findIndex(device => device._id === this.selectedDevice()?._id);
  });
  moduleId = computed(() => this.navigationService.moduleIdSig());
  smallNav = input(false);
  shown: NavigationPopovers = {
    portfolio: false,
    country: false,
    site: false,
    device: false,
  };
  urlSig = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  showNav = computed(() => {
    const moduleId = this.navigationService.moduleIdSig();
    const url = this.urlSig();
    const onReportDetail = url.startsWith('/reports/detail');
    return moduleId !== 'settings' && !onReportDetail;
  });
  portfolioListSignal = this.navigationDataService.getPortfolioList();
  portfolioList = computed(
    () =>
      this.portfolioListSignal()?.toSorted((a, b) =>
        a?.name.replace(/\s/g, '').localeCompare(b.name.replace(/\s/g, '')),
      ) || [],
  );
  selectedPortfolio = computed(() => this.navigationService.selectedPortfolioSig());
  portfolioName = computed<string>(() => this.navigationService.selectedPortfolioSig()?.name);
  portfolioOptions = computed(() => this.portfolioList().map(portfolio => portfolio.name));
  // for countries we will have custom dropdown as it should have Regions submenu
  countriesOptions = computed(() => this.countryList());

  // Search state for countries
  countrySearch = signal('');
  filteredCountriesOptions = computed(() => {
    const term = this.countrySearch().toLowerCase();
    if (!term) return this.countryList();
    return this.countryList().filter(
      c => c.name.toLowerCase().includes(term) || c.regions?.some(r => r.name.toLowerCase().includes(term)),
    );
  });
  hasMultipleCountryOptions = computed(() => {
    const countries = this.countryList();
    return countries.length > 1 || (countries.length === 1 && (countries[0].regions?.length ?? 0) > 1);
  });
  sitesOptions = computed(() => this.filteredSites().map(site => site.name));
  devicesList = computed(() =>
    (this.selectedSite()?.Devices ?? []).toSorted((first, second) => {
      const statusDifference =
        deviceStatusSortRank(this.deviceStatuses().get(first._id)) -
        deviceStatusSortRank(this.deviceStatuses().get(second._id));

      return (
        statusDifference ||
        (first.displayName ?? '').localeCompare(second.displayName ?? '', undefined, { sensitivity: 'base' })
      );
    }),
  );
  devicesOptions = computed(() => this.devicesList().map(device => device.displayName) || []);
  deviceSearch = signal('');
  filteredDevicesOptions = computed(() => {
    const term = this.deviceSearch().trim().toLowerCase();
    return term
      ? (this.devicesList() ?? []).filter(device => device.displayName?.toLowerCase().includes(term))
      : (this.devicesList() ?? []);
  });
  hasNextTurbine = computed(() => this.selectedDeviceIndex() < this.devicesList()?.length - 1);
  hasPrevTurbine = computed(() => this.selectedDeviceIndex() > 0);

  navigateToPortfolioLevel(force = false) {
    if (force || this.navigationService.levelSig()() !== 'portfolio') {
      const id = this.localStorageService.getSelectedPortfolioId();
      this.router.navigate([`${this.moduleId()}/portfolio/${id}`]);
    }
  }

  navigateToCountryLevel(force = false) {
    if (force || this.navigationService.levelSig()() !== 'country') {
      const portfolioId = this.navigationService.selectedPortfolioSig()?._id;
      const country = this.selectedCountry() || this.countryList()?.[0];
      const countryId = this.navigationService.idSig() || country?._id;
      const regionId = this.navigationService.regionIdSig() || country?.regions?.[0]?._id;

      if (!portfolioId || (!countryId && !regionId)) {
        return;
      }

      if (regionId) {
        this.router.navigate([`${this.moduleId()}/portfolio/${portfolioId}/region/${regionId}`]);
      } else {
        this.router.navigate([`${this.moduleId()}/portfolio/${portfolioId}/country/${countryId}`]);
      }
    }
  }

  navigateToSiteLevel(force = false) {
    if (force || this.navigationService.levelSig()() !== 'site') {
      const portfolioId = this.navigationService.selectedPortfolioSig()?._id;
      const id = this.selectedSite()?._id;
      this.router.navigate([`${this.moduleId()}/portfolio/${portfolioId}/site/${id}`]);
    }
  }

  navigateToDeviceLevel(force = false) {
    if (force || this.navigationService.levelSig()() !== 'device') {
      const portfolioId = this.navigationService.selectedPortfolioSig()?._id;
      const id = this.selectedDevice()?._id;
      const deviceTab = this.router.url.split('/').pop() ? `/${this.router.url.split('/').pop()}` : '';
      this.router.navigate([`${this.moduleId()}/portfolio/${portfolioId}/device/${id}` + deviceTab]);
    }
  }

  selectPortfolio(event) {
    const portfolio = this.portfolioList()[event.index];
    this.navigationService.setSelectedPortfolio(portfolio);
    this.navigateToPortfolioLevel(true);
  }

  selectCountry(event) {
    const selectedCountry = this.countryList()[event.index];
    this.navigationService.setSelectedCountry(selectedCountry);
    this.navigateToCountryLevel(true);
  }

  selectSites(event) {
    const selectedSite = this.filteredSites()[event.index];
    this.navigationService.setSelectedSite(selectedSite);
    this.navigateToSiteLevel(true);
  }

  selectDevice(device: Device): void {
    this.navigationService.setSelectedDevice(device);
    this.navigateToDeviceLevel(true);
  }

  deviceStatusColor(status?: string | null): string {
    const colors: Record<string, string> = {
      ACTIVE: '#00a6d6',
      BEING_UPDATED: '#ff6b43',
      DECOMMISSIONED: '#8aaec4',
      INACTIVE: '#8aaec4',
      ON_HOLD: '#ff6b43',
      PENDING: '#ff6b43',
      TOWER_ONLY: '#ff6b43',
      UNKNOWN: '#ff6b43',
    };
    return colors[status ?? 'UNKNOWN'] ?? colors['UNKNOWN'];
  }

  isLevelSelected(level: 'portfolio' | 'country' | 'region' | 'site' | 'device'): boolean {
    return this.navigationService.levelSig()() === level;
  }

  goToNextTurbine(isForward: boolean): void {
    const nextTurbineIndex = isForward ? this.selectedDeviceIndex() + 1 : this.selectedDeviceIndex() - 1;
    if (nextTurbineIndex < 0 || nextTurbineIndex >= this.devicesList()?.length) {
      return;
    }
    this.navigationService.setSelectedDevice(this.devicesList()[nextTurbineIndex]);
    this.navigateToDeviceLevel(true);
  }

  isTextOverflowing(element?: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return element.scrollWidth > element.clientWidth;
  }

  selectCountryFromDropdown(country: any) {
    this.navigationService.setSelectedCountry(country);
    this.navigationService.setSelectedRegion(null);
    this.navigateToCountryLevel(true);
  }

  selectRegion(country: any, region: any) {
    this.navigationService.setSelectedCountry(country);
    this.navigationService.setSelectedRegion(region);
    this.navigateToCountryLevel(true);
  }
}
